import { t } from "@/i18n-js/instance";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import type { Appearance } from "@circle-react-shared/BuilderUI/Store/interfaceSlice";
import { Button } from "@circle-react-shared/uikit/Button";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Modal } from "@circle-react-uikit/ModalV2";
import type { SiteTheme } from "./constants";
import { useUpdatePageTheme } from "./useUpdatePageTheme";

interface PageThemeConfirmationModalProps {
  appearance: Appearance;
  currentCanvasTheme: SiteTheme;
  pageId: number;
}

const PageThemeConfirmationModalComponent = ({
  currentCanvasTheme,
  appearance,
  pageId,
}: PageThemeConfirmationModalProps) => {
  const modal = useModal();

  const { mutate: updatePageTheme, isLoading: isUpdatingTheme } =
    useUpdatePageTheme({ pageId });

  return (
    <Modal
      isOpen={modal.visible}
      onClose={() => {
        if (isUpdatingTheme) return;

        void modal.hide();
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title size="sm" className="font-bold">
            {t(
              "builder_ui.theme_configuration.update_page_theme_confirmation_modal.title",
            )}
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton
              onClick={modal.hide}
              disabled={isUpdatingTheme}
            />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="overflow-y-auto pb-11 md:!px-8">
          <Typography.BodyMd color="text-dark">
            {t(
              "builder_ui.theme_configuration.update_page_theme_confirmation_modal.description",
            )}
          </Typography.BodyMd>
        </Modal.Body>
        <Modal.Footer className="border-primary flex w-full justify-end gap-4 border-t">
          <Button
            variant="secondary"
            onClick={modal.hide}
            disabled={isUpdatingTheme}
          >
            {t(
              "builder_ui.theme_configuration.update_page_theme_confirmation_modal.secondary_cta",
            )}
          </Button>
          <Button
            variant="circle"
            onClick={() =>
              updatePageTheme(
                {
                  id: currentCanvasTheme.value,
                  appearance,
                },
                {
                  onSuccess: () => {
                    void modal.hide();
                  },
                },
              )
            }
            disabled={isUpdatingTheme}
          >
            {t(
              "builder_ui.theme_configuration.update_page_theme_confirmation_modal.primary_cta",
            )}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const PageThemeConfirmationModal = ModalManager.create(
  ({
    currentCanvasTheme,
    pageId,
    appearance,
  }: PageThemeConfirmationModalProps) => (
    <PageThemeConfirmationModalComponent
      pageId={pageId}
      appearance={appearance}
      currentCanvasTheme={currentCanvasTheme}
    />
  ),
);

export const usePageThemeConfirmationModal = () =>
  useModal(PageThemeConfirmationModal);
